import React, { useImperativeHandle, useMemo, useRef, useState } from "react";
import { RCCHookToast, RCCRefModalProps, RCCServicesApi } from "react-common-components";
import { useForm } from "react-hook-form";
import Modal from "src/components/Modal";
import ModalSide from "src/components/Modal/styles";
import { useTranslation } from "src/context/Translations/context";
import { getValidationErrors, standardErrorMessages } from "src/utils/errors";
import { useLoaderBackdrop } from "src/context/LoaderBackdrop/context";
import Title from "src/components/Title";
import { getTitleBudgetTypes } from "src/pages/FinancialManagement/interface";
import IModalRefIntegrationsFinancialManagement, { IIntegrationEntity, IModalIntegrationsFinancialManagement } from "./interface";
import Integrations from "./integrations";
import integrationSchema from "./schemas";

const ModalIntegrationsFinancialManagement: React.ForwardRefRenderFunction<IModalRefIntegrationsFinancialManagement, IModalIntegrationsFinancialManagement> = ({
	type
}, ref) => {
	const { translate } = useTranslation(); 
	const { addToast } = RCCHookToast();
	const { handleSubmit, register, reset, control, clearErrors, setError } = useForm(); 
	const { setLoading } = useLoaderBackdrop();
    
	const budgetId = useRef<number>(0);
	const budgetPlanId = useRef<number>(0);
	const budgetParentId = useRef<number | undefined>(undefined);
	const modalIntegrations = useRef<RCCRefModalProps>(null);
	const [subtitleModal, setSubtitleModal] = useState("");
	// const onCallbackFunction = useRef<Function>(() => {});
	const [integrations, setIntegrations] = useState<IIntegrationEntity[]>([] as IIntegrationEntity[]);
	const openModalIntegrationsFinancialManagement = async (id: number, subtitle: string, ofa001Id: number, parentId: number) => {
		try{
			setLoading(true);
			reset();
			budgetId.current = id;
			budgetPlanId.current = ofa001Id;
			budgetParentId.current = parentId;

			const {data} = await RCCServicesApi.getRequest({
				method: "FinancialManagement/GetIntegrationsInHierarchy",
				urlParameters: {
					id,
					budgetPlanId: ofa001Id,
					type
				}
			});
            
			setIntegrations(data.integrations);

			setSubtitleModal(`${getTitleBudgetTypes(type, translate)  }: ${data.code  } - ${  subtitle}`);
			// onCallbackFunction.current = onCallback || (() => {});
			
			setLoading(false);
			modalIntegrations.current?.openModal();
		}catch (err){
			setLoading(false);
			standardErrorMessages(err, translate("Erro ao buscar códigos de integração"), addToast);
		}
	};
	const onSubmit = async (data: any) => {
		try{
			clearErrors();

			await integrationSchema(translate).validate(data.integrations, {
				abortEarly: false
			});	

			await RCCServicesApi.postRequest({
				method: "FinancialManagement/InsertIntegrationsInHierarchy",
				body: {
					...data,
					id: budgetId.current,
					budgetPlanId: budgetPlanId.current,
					budgetParentId: budgetParentId.current,
					type
				}
			});

			addToast({
				color: "green",
				description: translate("Códigos de integração adicionados com sucesso"),
				title: translate("Sucesso")
			});

			modalIntegrations.current?.resetLoadingButton();
			modalIntegrations.current?.closeModal();
		}catch(err){
			modalIntegrations.current?.resetLoadingButton();
			if(getValidationErrors(err, setError)){
				addToast({
					title: translate("Erro"),
					description: translate("Títulos não podem ser repetidos"),
					color: "yellow"
				});
				return;
			}
			standardErrorMessages(err, translate("Erro ao salvar códigos de integração"), addToast);
		}
	};

	useImperativeHandle(ref, () => ({
		openModalIntegrationsFinancialManagement
	}));

	const MemorizedIntegrations = useMemo(() => <Integrations register={register} integrations={integrations} control={control} type={type} /> , [integrations]);

	return (
		<Modal
			ref={modalIntegrations}
			header={{
				title: translate("Integração"),
				subtitle: subtitleModal,
				icon: {
					name: "refresh"
				}
			}}
			onSubmit={handleSubmit(onSubmit)}
			styleInline={ModalSide}
		>
			<Title color="grey" size="xs" marginBottom={8}>
				{translate("Adicione códigos de integração para")} {subtitleModal}
			</Title>
			{MemorizedIntegrations}
		</Modal>
	);
};

export default React.forwardRef(ModalIntegrationsFinancialManagement);
